import React from "react";
import {
    Box,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import {NodeViewContent} from "@tiptap/react";
import type {NodeViewProps} from "@tiptap/react";
import type {ChainedCommands} from "@tiptap/core";
import {useTranslation} from "react-i18next";
import {parseColorAttribute} from "../shared/blockAttributes";
import PrintoutBlockShell, {usePrintoutNodeSelection} from "../shared/PrintoutBlockShell";
import {
    AddColumnLeftIcon,
    AddColumnRightIcon,
    AddRowAboveIcon,
    AddRowBelowIcon,
} from "../shared/printoutMaterialSymbols";
import {
    getPrintoutTableColumnCount,
    getPrintoutTableRowCount,
} from "./printoutTableCommands";
import {
    MAX_TABLE_COLUMNS,
    MAX_TABLE_ROWS,
    MIN_COLUMN_WIDTH_PX,
    MIN_TABLE_COLUMNS,
    MIN_TABLE_ROWS,
    normalizeColumnWidths,
    resolveTableBorderColors,
    type PrintoutTableNodeAttrs,
} from "./printoutTableOptions";

const TABLE_EDITABLE_CONTENT_SELECTOR = "[data-printout-table-editable-content]";

const PrintoutTableNodeView = (props: NodeViewProps) => {
    const {editor, getPos, node} = props;
    const {t} = useTranslation();
    const tableWrapperRef = React.useRef<HTMLDivElement | null>(null);
    const resizeCleanupRef = React.useRef<(() => void) | null>(null);
    const editable = editor.isEditable;
    const tableWidthClassName = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
    const rows = getPrintoutTableRowCount(node);
    const columns = getPrintoutTableColumnCount(node);
    const attrs = node.attrs as PrintoutTableNodeAttrs;
    const backgroundColor = parseColorAttribute(attrs.backgroundColor);
    const borderColors = resolveTableBorderColors(attrs);
    const storedWidths = normalizeColumnWidths(attrs.colWidths, columns);
    const [draftWidths, setDraftWidths] = React.useState<number[] | null>(null);
    const [rowMenuAnchorEl, setRowMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [columnMenuAnchorEl, setColumnMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [resizingBoundary, setResizingBoundary] = React.useState<number | null>(null);
    const widths = draftWidths ?? storedWidths;
    // One state object: whole-table selection, caret-inside, and nested node ownership must
    // invalidate together or the table keeps chrome that a nested node view now owns.
    const tableSelection = usePrintoutNodeSelection(editor, getPos, node.type.name);
    const tableCellIsActive = editable
        && tableSelection.selectionInside
        && !tableSelection.nestedSelectionOwner;
    const tableStructureControlsActive = tableCellIsActive || resizingBoundary !== null;
    const tableActive = editable && (
        tableSelection.nodeSelected || tableSelection.selectionInside || resizingBoundary !== null
    );
    const tableMenuActive = editable && (
        tableSelection.nodeSelected
        || resizingBoundary !== null
        // Any nested node view — an inline variable chip or a nested block — owns its own chrome.
        || (tableSelection.selectionInside && !tableSelection.nestedSelectionOwner)
    );
    const tableColumnWidthRules = widths.map((width, columnIndex) => [
        `.printout-table-widths-${tableWidthClassName} tr > th:nth-child(${columnIndex + 1}),`,
        `.printout-table-widths-${tableWidthClassName} tr > td:nth-child(${columnIndex + 1}) {`,
        `width: ${(width * 100).toFixed(4)}%;`,
        `}`,
    ].join("\n")).join("\n");

    const isSelectionInsideTable = React.useCallback(() => {
        const position = getPos();

        if (typeof position !== "number") return false;

        const {selection} = editor.state;

        return selection.from > position && selection.to < position + node.nodeSize;
    }, [editor, getPos, node.nodeSize]);

    React.useEffect(() => () => {
        resizeCleanupRef.current?.();
        resizeCleanupRef.current = null;
    }, []);

    React.useEffect(() => {
        if (tableStructureControlsActive) return;

        // A detached menu anchor would reopen the old cell menu when the user later
        // places the caret back inside the table.
        setRowMenuAnchorEl(null);
        setColumnMenuAnchorEl(null);
    }, [tableStructureControlsActive]);

    const updateTableAttributes = (nextAttrs: Partial<PrintoutTableNodeAttrs>) => {
        props.updateAttributes(nextAttrs);
    };

    const runTableCommand = (command: (chain: ChainedCommands) => ChainedCommands) => {
        if (!isSelectionInsideTable()) {
            setRowMenuAnchorEl(null);
            setColumnMenuAnchorEl(null);
            return;
        }

        command(editor.chain().focus()).run();
        setRowMenuAnchorEl(null);
        setColumnMenuAnchorEl(null);
    };

    const setColWidths = (next: number[]) => {
        updateTableAttributes({colWidths: normalizeColumnWidths(next, columns)});
    };

    const handleResizeStart = (boundaryIndex: number) => (event: React.PointerEvent) => {
        if (!editable) return;

        event.preventDefault();
        event.stopPropagation();
        resizeCleanupRef.current?.();

        const tableWidthPx = tableWrapperRef.current?.offsetWidth ?? 0;
        if (!tableWidthPx) return;

        const startX = event.clientX;
        const startWidths = [...widths];
        const pairTotal = startWidths[boundaryIndex] + startWidths[boundaryIndex + 1];
        const minFraction = Math.min(pairTotal / 2, MIN_COLUMN_WIDTH_PX / tableWidthPx);

        setResizingBoundary(boundaryIndex);

        const onMove = (moveEvent: PointerEvent) => {
            const deltaFraction = (moveEvent.clientX - startX) / tableWidthPx;
            const leftWidth = Math.max(
                minFraction,
                Math.min(pairTotal - minFraction, startWidths[boundaryIndex] + deltaFraction),
            );
            const nextWidths = [...startWidths];
            nextWidths[boundaryIndex] = leftWidth;
            nextWidths[boundaryIndex + 1] = pairTotal - leftWidth;
            setDraftWidths(nextWidths);
        };
        let onUp: () => void;
        let onCancel: () => void;

        const cleanupResizeListeners = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onCancel);
            window.removeEventListener("blur", onCancel);
            resizeCleanupRef.current = null;
        };

        const finishResize = (commitDraft: boolean) => {
            cleanupResizeListeners();

            if (!commitDraft) {
                setDraftWidths(null);
                setResizingBoundary(null);
                return;
            }

            setDraftWidths((current) => {
                if (current) setColWidths(current);

                return null;
            });
            setResizingBoundary(null);
        };

        onUp = () => {
            finishResize(true);
        };
        onCancel = () => {
            finishResize(false);
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onCancel);
        window.addEventListener("blur", onCancel);
        resizeCleanupRef.current = cleanupResizeListeners;
    };

    const quickActions = (
        <>
            <Tooltip title={t("Rows")}>
                <IconButton
                    aria-label={t("Rows")}
                    className="printout-block-menu-icon"
                    onClick={(event) => setRowMenuAnchorEl(event.currentTarget)}
                    size="small"
                >
                    <TableRowsOutlinedIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={rowMenuAnchorEl}
                onClose={() => setRowMenuAnchorEl(null)}
                open={Boolean(rowMenuAnchorEl)}
            >
                <MenuItem
                    disabled={rows >= MAX_TABLE_ROWS}
                    onClick={() => runTableCommand(chain => chain.addRowBefore())}
                >
                    <ListItemIcon>
                        <AddRowAboveIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Insert row above")}
                </MenuItem>
                <MenuItem
                    disabled={rows >= MAX_TABLE_ROWS}
                    onClick={() => runTableCommand(chain => chain.addRowAfter())}
                >
                    <ListItemIcon>
                        <AddRowBelowIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Insert row below")}
                </MenuItem>
                <MenuItem
                    disabled={rows <= MIN_TABLE_ROWS}
                    onClick={() => runTableCommand(chain => chain.deleteRow())}
                    sx={{color: "error.main"}}
                >
                    <ListItemIcon sx={{color: "inherit"}}>
                        <DeleteOutlineIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Delete row")}
                </MenuItem>
            </Menu>

            <Tooltip title={t("Columns")}>
                <IconButton
                    aria-label={t("Columns")}
                    className="printout-block-menu-icon"
                    onClick={(event) => setColumnMenuAnchorEl(event.currentTarget)}
                    size="small"
                >
                    <ViewColumnOutlinedIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={columnMenuAnchorEl}
                onClose={() => setColumnMenuAnchorEl(null)}
                open={Boolean(columnMenuAnchorEl)}
            >
                <MenuItem
                    disabled={columns >= MAX_TABLE_COLUMNS}
                    onClick={() => runTableCommand(chain => chain.addColumnBefore())}
                >
                    <ListItemIcon>
                        <AddColumnLeftIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Insert column left")}
                </MenuItem>
                <MenuItem
                    disabled={columns >= MAX_TABLE_COLUMNS}
                    onClick={() => runTableCommand(chain => chain.addColumnAfter())}
                >
                    <ListItemIcon>
                        <AddColumnRightIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Insert column right")}
                </MenuItem>
                <MenuItem
                    disabled={columns <= MIN_TABLE_COLUMNS}
                    onClick={() => runTableCommand(chain => chain.deleteColumn())}
                    sx={{color: "error.main"}}
                >
                    <ListItemIcon sx={{color: "inherit"}}>
                        <DeleteOutlineIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Delete column")}
                </MenuItem>
            </Menu>
        </>
    );

    return (
        <PrintoutBlockShell
            {...props}
            active={tableActive}
            allowEditableContent={editable}
            blockType="table"
            editableContentSelector={TABLE_EDITABLE_CONTENT_SELECTOR}
            icon={<TableChartOutlinedIcon fontSize="small"/>}
            /* "Table" is the restaurant table elsewhere in the app and translates to "Tisch";
               this block is a data table, so it shares the palette's own key. */
            label="Table block"
            menuActive={tableMenuActive}
            quickActions={tableStructureControlsActive ? quickActions : undefined}
        >
            <Box
                className="printout-table-block-wrapper"
                ref={tableWrapperRef}
                style={{
                    "--printout-table-background-color": backgroundColor ?? "transparent",
                    "--printout-table-border-top-color": borderColors.top,
                    "--printout-table-border-right-color": borderColors.right,
                    "--printout-table-border-bottom-color": borderColors.bottom,
                    "--printout-table-border-left-color": borderColors.left,
                } as React.CSSProperties}
            >
                <style>{tableColumnWidthRules}</style>
                <NodeViewContent
                    as="table"
                    className={`printout-table-block printout-table-widths-${tableWidthClassName}`}
                    data-printout-table-editable-content
                    style={{whiteSpace: "normal"}}
                />
                {tableStructureControlsActive && widths.slice(0, -1).map((_, boundaryIndex) => {
                    const left = widths
                        .slice(0, boundaryIndex + 1)
                        .reduce((sum, width) => sum + width, 0);

                    return (
                        <Box
                            className="printout-table-block-resize-handle"
                            contentEditable={false}
                            data-printout-block-action
                            key={boundaryIndex}
                            onPointerDown={handleResizeStart(boundaryIndex)}
                            sx={{left: `${left * 100}%`}}
                        />
                    );
                })}
            </Box>
        </PrintoutBlockShell>
    );
};

export default PrintoutTableNodeView;
